import { useCallback, useMemo, useState } from 'react';
import {
  useUpdateChatbotLogoMutation,
  useUpdateChatbotMutation,
} from '../../../features/chatbots/mutations/useCreateChatbotMutation';
import { downloadChatbotLogo } from '../../../features/chatbots/downloadChatbotLogo';
import { useProfilePhotoFormState } from '../../../features/user/useProfilePhotoFormState';
import { useI18n } from '../../../i18n/useI18n';
import {
  DEFAULT_CHATBOT_PRIMARY_COLOR,
  type ChatbotConfigUpdate,
  type ChatbotPlacement,
  type ChatbotWithIdentity,
} from '../../../types/chatbot';
import { TENANT_LOGO_MAX_BYTES } from '../../../utils/imageUtils';
import {
  computeChatbotConfigurationDirtyFlags,
  normalizeOptionalText,
  parseCorsAllowedOrigins,
} from './chatbotConfigurationHelpers';
import {
  saveChatbotConfigUpdates,
  saveChatbotLogoUpdate,
} from './chatbotConfigurationSave';
import {
  resolveRagMaxResults,
  resolveRagMinScore,
} from '../../../utils/ragRetrievalSettings';

type UseChatbotConfigurationTabArgs = {
  chatbot: ChatbotWithIdentity;
  showPreview: boolean;
  onShowPreviewChange: (value: boolean) => void;
};

export function useChatbotConfigurationTab({
  chatbot,
  showPreview,
  onShowPreviewChange,
}: UseChatbotConfigurationTabArgs) {
  const { translate } = useI18n();
  const updateChatbotMutation = useUpdateChatbotMutation();
  const updateChatbotLogoMutation = useUpdateChatbotLogoMutation();
  const isSaving = updateChatbotMutation.isPending;
  const isLogoSaving = updateChatbotLogoMutation.isPending;

  const loadLogo = useCallback(
    () => downloadChatbotLogo(chatbot.id),
    [chatbot.id],
  );

  const {
    previewSrc: logoDisplaySrc,
    photoError: logoError,
    photoChanged: isLogoDirty,
    pendingFile: logoFile,
    removePhoto: removeLogo,
    handlePhotoChange: handleLogoChange,
    handleRemovePhoto: handleRemoveLogo,
    resetPhotoEdits: resetLogoEdits,
  } = useProfilePhotoFormState({
    loadPhoto: loadLogo,
    translate,
    invalidTypeKey: 'chatbots.settings.logo.invalidType',
    tooLargeKey: 'chatbots.settings.logo.tooLarge',
    maxBytes: TENANT_LOGO_MAX_BYTES,
    reloadKey: chatbot.stamp ?? chatbot.id,
  });

  const [draftShowPreview, setDraftShowPreview] = useState(showPreview);
  const [previousShowPreview, setPreviousShowPreview] = useState(showPreview);
  const [isPublic, setIsPublic] = useState(chatbot.isPublic === true);
  const [displayName, setDisplayName] = useState(
    normalizeOptionalText(chatbot.displayName),
  );
  const [welcomeMessage, setWelcomeMessage] = useState(
    normalizeOptionalText(chatbot.welcomeMessage),
  );
  const [systemPrompt, setSystemPrompt] = useState(
    normalizeOptionalText(chatbot.systemPrompt),
  );
  const [maxResults, setMaxResults] = useState(() =>
    resolveRagMaxResults(chatbot.maxResults),
  );
  const [minScore, setMinScore] = useState(() =>
    resolveRagMinScore(chatbot.minScore),
  );
  const [primaryColor, setPrimaryColor] = useState(
    normalizeOptionalText(chatbot.primaryColor) ||
      DEFAULT_CHATBOT_PRIMARY_COLOR,
  );
  const [placement, setPlacement] = useState<ChatbotPlacement>(
    chatbot.placement ?? 'BOTTOM_RIGHT',
  );
  const [origins, setOrigins] = useState<string[]>(() =>
    parseCorsAllowedOrigins(chatbot.corsAllowedOrigins),
  );
  const [newOrigin, setNewOrigin] = useState('');

  if (showPreview !== previousShowPreview) {
    setPreviousShowPreview(showPreview);
    setDraftShowPreview(showPreview);
  }

  const originalOrigins = useMemo(
    () => parseCorsAllowedOrigins(chatbot.corsAllowedOrigins),
    [chatbot.corsAllowedOrigins],
  );

  const {
    isPreviewDirty,
    isPublicDirty,
    isAiConfigurationDirty,
    isCorsDirty,
    isAppearanceDirty,
    isAppearanceCardDirty,
  } = computeChatbotConfigurationDirtyFlags({
    chatbot,
    draftShowPreview,
    showPreview,
    isPublic,
    displayName,
    welcomeMessage,
    systemPrompt,
    maxResults,
    minScore,
    primaryColor,
    placement,
    origins,
    originalOrigins,
    isLogoDirty,
  });

  const trimmedNewOrigin = newOrigin.trim();
  const canAddOrigin =
    trimmedNewOrigin !== '' && !origins.includes(trimmedNewOrigin);

  const discardLabel = translate('chatbots.settings.discard');
  const saveLabel = translate('chatbots.settings.save');

  const saveUpdates = (
    updates: ChatbotConfigUpdate,
    successKey: string,
    errorKey: string,
  ) => {
    saveChatbotConfigUpdates({
      updateChatbotMutation,
      chatbot,
      updates,
      translate,
      successKey,
      errorKey,
    });
  };

  const handleAddOrigin = () => {
    if (!canAddOrigin) {
      return;
    }
    setOrigins((current) => [...current, trimmedNewOrigin]);
    setNewOrigin('');
  };

  const handleRemoveOrigin = (origin: string) => {
    setOrigins((current) => current.filter((entry) => entry !== origin));
  };

  const handleSaveLogo = () => {
    if (!isLogoDirty || isLogoSaving) {
      return;
    }
    saveChatbotLogoUpdate({
      updateChatbotLogoMutation,
      chatbotId: chatbot.id,
      logoFile,
      removeLogo,
      translate,
    });
  };
  const handleDiscardPreview = () => {
    setDraftShowPreview(showPreview);
  };

  const handleSavePreview = () => {
    if (!isPreviewDirty) {
      return;
    }
    onShowPreviewChange(draftShowPreview);
  };

  const handleDiscardPublic = () => {
    setIsPublic(chatbot.isPublic === true);
  };

  const handleDiscardAppearance = () => {
    setDisplayName(normalizeOptionalText(chatbot.displayName));
    setWelcomeMessage(normalizeOptionalText(chatbot.welcomeMessage));
    setPrimaryColor(
      normalizeOptionalText(chatbot.primaryColor) ||
        DEFAULT_CHATBOT_PRIMARY_COLOR,
    );
    setPlacement(chatbot.placement ?? 'BOTTOM_RIGHT');
    resetLogoEdits();
  };

  const handleSaveAppearance = () => {
    if (isLogoDirty) {
      handleSaveLogo();
    }
    if (isAppearanceDirty) {
      saveUpdates(
        {
          displayName: displayName.trim() || null,
          primaryColor: primaryColor.trim() || null,
          welcomeMessage: welcomeMessage.trim() || null,
          placement,
        },
        'chatbots.settings.appearance.saveSuccessTitle',
        'chatbots.settings.appearance.saveErrorTitle',
      );
    }
  };

  const handleDiscardAiConfiguration = () => {
    setSystemPrompt(normalizeOptionalText(chatbot.systemPrompt));
    setMaxResults(resolveRagMaxResults(chatbot.maxResults));
    setMinScore(resolveRagMinScore(chatbot.minScore));
  };

  const handleDiscardCors = () => {
    setOrigins(parseCorsAllowedOrigins(chatbot.corsAllowedOrigins));
    setNewOrigin('');
  };

  return {
    chatbotId: chatbot.id,
    draftShowPreview,
    setDraftShowPreview,
    isPreviewDirty,
    handleDiscardPreview,
    handleSavePreview,
    isPublic,
    setIsPublic,
    isPublicDirty,
    handleDiscardPublic,
    savePublic: () =>
      saveUpdates(
        { isPublic },
        'chatbots.settings.public.saveSuccessTitle',
        'chatbots.settings.public.saveErrorTitle',
      ),
    logoDisplaySrc,
    isLogoSaving,
    logoError,
    handleLogoChange,
    handleRemoveLogo,
    displayName,
    setDisplayName,
    primaryColor,
    setPrimaryColor,
    welcomeMessage,
    setWelcomeMessage,
    placement,
    setPlacement,
    isAppearanceCardDirty,
    isSaving,
    isAppearanceSaveDisabled:
      !isAppearanceCardDirty || isLogoSaving || isSaving || logoError != null,
    handleDiscardAppearance,
    handleSaveAppearance,
    systemPrompt,
    setSystemPrompt,
    maxResults,
    setMaxResults,
    minScore,
    setMinScore,
    isAiConfigurationDirty,
    handleDiscardAiConfiguration,
    saveAiConfiguration: () =>
      saveUpdates(
        {
          systemPrompt: systemPrompt.trim() || null,
          maxResults,
          minScore,
        },
        'chatbots.settings.aiConfiguration.saveSuccessTitle',
        'chatbots.settings.aiConfiguration.saveErrorTitle',
      ),
    origins,
    newOrigin,
    setNewOrigin,
    canAddOrigin,
    isCorsDirty,
    handleAddOrigin,
    handleRemoveOrigin,
    handleDiscardCors,
    saveCors: () =>
      saveUpdates(
        { corsAllowedOrigins: origins },
        'chatbots.settings.cors.saveSuccessTitle',
        'chatbots.settings.cors.saveErrorTitle',
      ),
    discardLabel,
    saveLabel,
  };
}
